/**
 * Presentational sign-in recipe shared by the EA and TIM auth adapters.
 * Copy and callbacks arrive as props; authentication does not live here.
 */

import { useRef } from 'react'
import type { CSSProperties, FormEvent, ReactElement, ReactNode } from 'react'
import { Button, Spinner, makeStyles, tokens } from '@fluentui/react-components'
import { WINDOW_BREAKPOINTS, useContainerWidth } from '../sdk/useContainerWidth'
import { ConsoleLogo } from './ConsoleMark'
import {
  CARD_HAIRLINE,
  CARD_SHADOW,
  LOGIN_BACKDROP,
  LOGIN_BACKDROP_IMAGE,
  LOGIN_PANEL_GRADIENT,
  LOGIN_PANEL_HIGHLIGHT,
  ON_BRAND,
  ON_BRAND_FAINT,
  ON_BRAND_RULE,
  ON_BRAND_SUBTLE,
  TOGGLE_FILL,
  TOGGLE_STROKE,
} from './loginPalette'

const useStyles = makeStyles({
  stage: {
    position: 'fixed',
    inset: 0,
    zIndex: 80,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxSizing: 'border-box',
    padding: '24px',
    overflowY: 'auto',
    backgroundColor: LOGIN_BACKDROP,
    backgroundImage: LOGIN_BACKDROP_IMAGE,
    '@media (max-width: 719px)': {
      alignItems: 'flex-start',
      paddingTop: '16px',
      paddingRight: '16px',
      paddingBottom: '16px',
      paddingLeft: '16px',
    },
  },
  toggle: {
    position: 'absolute',
    top: '20px',
    right: '20px',
    minWidth: '44px',
    width: '44px',
    height: '44px',
    padding: 0,
    borderRadius: '9px',
    cursor: 'pointer',
    color: ON_BRAND,
    fontSize: '15px',
    '@media (max-width: 719px)': {
      top: '12px',
      right: '12px',
    },
  },
  card: {
    display: 'flex',
    width: '920px',
    maxWidth: 'calc(100vw - 48px)',
    minHeight: '540px',
    borderRadius: '20px',
    overflow: 'hidden',
    boxShadow: CARD_SHADOW,
    border: `1px solid ${CARD_HAIRLINE}`,
    '@media (max-width: 719px)': {
      width: '100%',
      maxWidth: '480px',
      minHeight: 0,
      marginTop: '52px',
      marginRight: 'auto',
      marginBottom: '16px',
      marginLeft: 'auto',
    },
  },
  brand: {
    flex: '0 0 46%',
    boxSizing: 'border-box',
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'flex-start',
    padding: '48px 44px',
    backgroundImage: LOGIN_PANEL_GRADIENT,
    overflow: 'hidden',
    '@media (max-width: 719px)': {
      display: 'none',
    },
  },
  highlight: {
    position: 'absolute',
    inset: 0,
    backgroundImage: LOGIN_PANEL_HIGHLIGHT,
  },
  brandContent: {
    position: 'relative',
    zIndex: 1,
  },
  lockup: {
    color: ON_BRAND,
  },
  rule: {
    height: '1px',
    width: '62px',
    backgroundColor: ON_BRAND_RULE,
    margin: '22px 0 18px',
  },
  serviceName: {
    fontSize: '23px',
    fontWeight: 300,
    color: ON_BRAND,
    lineHeight: 1.25,
  },
  subtitle: {
    fontSize: '15px',
    fontWeight: 300,
    color: ON_BRAND_SUBTLE,
    marginTop: '4px',
  },
  description: {
    fontSize: '12px',
    color: ON_BRAND_FAINT,
    marginTop: '20px',
    maxWidth: '240px',
    lineHeight: 1.6,
  },
  form: {
    flex: '0 0 54%',
    boxSizing: 'border-box',
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    padding: '48px 46px',
    backgroundColor: tokens.colorNeutralBackground1,
    '@media (max-width: 719px)': {
      flexBasis: '100%',
      width: '100%',
      justifyContent: 'flex-start',
      paddingTop: '36px',
      paddingRight: '24px',
      paddingBottom: '28px',
      paddingLeft: '24px',
    },
  },
  heading: {
    fontSize: '24px',
    fontWeight: 700,
    color: tokens.colorNeutralForeground1,
    marginBottom: '24px',
  },
  failure: {
    fontSize: '12px',
    color: tokens.colorPaletteRedForeground1,
    backgroundColor: tokens.colorPaletteRedBackground1,
    border: `1px solid ${tokens.colorPaletteRedBorder1}`,
    borderRadius: '8px',
    padding: '9px 11px',
    marginBottom: '16px',
  },
  label: {
    display: 'block',
    fontSize: '11.5px',
    fontWeight: 600,
    color: tokens.colorNeutralForeground3,
    marginBottom: '6px',
  },
  input: {
    width: '100%',
    height: '44px',
    boxSizing: 'border-box',
    padding: '0 12px',
    fontSize: '13.5px',
    fontFamily: 'inherit',
    color: tokens.colorNeutralForeground1,
    backgroundColor: tokens.colorNeutralBackground3,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: '8px',
    marginBottom: '14px',
    outlineStyle: 'none',
    ':focus': {
      borderTopColor: tokens.colorBrandStroke1,
      borderRightColor: tokens.colorBrandStroke1,
      borderBottomColor: tokens.colorBrandStroke1,
      borderLeftColor: tokens.colorBrandStroke1,
    },
  },
  primary: {
    width: '100%',
    height: '44px',
    border: 'none',
    color: ON_BRAND,
    borderRadius: '9px',
    fontSize: '14px',
    fontWeight: 600,
    fontFamily: 'inherit',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    ':disabled': {
      cursor: 'default',
      opacity: 0.6,
    },
  },
  dividerRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    margin: '22px 0',
  },
  dividerLine: {
    flex: 1,
    height: '1px',
    backgroundColor: tokens.colorNeutralStroke2,
  },
  dividerWord: {
    fontSize: '12px',
    color: tokens.colorNeutralForeground4,
  },
  secondary: {
    width: '100%',
    height: '44px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '10px',
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    backgroundColor: tokens.colorNeutralBackground1,
    color: tokens.colorNeutralForeground1,
    borderRadius: '9px',
    fontSize: '13.5px',
    fontWeight: 600,
    fontFamily: 'inherit',
    cursor: 'pointer',
    ':disabled': {
      cursor: 'default',
      color: tokens.colorNeutralForegroundDisabled,
    },
  },
  secondaryStatus: {
    fontSize: '11.5px',
    color: tokens.colorNeutralForeground4,
    textAlign: 'center',
    marginTop: '10px',
  },
  footnote: {
    fontSize: '11.5px',
    color: tokens.colorNeutralForeground4,
    textAlign: 'center',
    marginTop: '24px',
    lineHeight: 1.6,
  },
})

interface TextFieldSlot {
  id: string
  label: string
  type: 'email' | 'password'
  value: string
  autoComplete: string
  placeholder?: string
  autoFocus?: boolean
  onChange: (value: string) => void
}

export interface SignInLayoutProps {
  brand: {
    serviceName: string
    subtitle: string
    description: string
  }
  title: string
  error?: string | null
  fields: [TextFieldSlot, TextFieldSlot]
  primaryAction: {
    label: string
    busy: boolean
    disabled: boolean
    style?: CSSProperties
  }
  dividerLabel: string
  secondaryAction: {
    label: string
    disabled: boolean
    icon?: ReactElement
    status?: string | null
    onClick: () => void
  }
  footnote: ReactNode
  themeControl: {
    label: string
    glyph: ReactNode
    onClick: () => void
  }
  onSubmit: (event: FormEvent) => void
}

export function SignInLayout({
  brand,
  dividerLabel,
  error,
  fields,
  footnote,
  onSubmit,
  primaryAction,
  secondaryAction,
  themeControl,
  title,
}: SignInLayoutProps) {
  const styles = useStyles()
  const stage = useRef<HTMLDivElement>(null)
  const available = useContainerWidth(stage)
  const mobile = available > 0 && available < WINDOW_BREAKPOINTS.md
  return (
    <div
      ref={stage}
      className={styles.stage}
      data-testid="sign-in-stage"
      data-scroll-safe="true"
      data-layout={mobile ? 'single-form' : 'split'}
    >
      <Button
        className={styles.toggle}
        appearance="transparent"
        style={{ backgroundColor: TOGGLE_FILL, border: `1px solid ${TOGGLE_STROKE}` }}
        title={themeControl.label}
        aria-label={themeControl.label}
        onClick={themeControl.onClick}
      >
        {themeControl.glyph}
      </Button>

      <div className={styles.card} data-testid="sign-in-card">
        {!mobile ? (
          <aside className={styles.brand} data-testid="sign-in-brand">
            <div className={styles.highlight} />
            <div className={styles.brandContent}>
              <ConsoleLogo height={40} className={styles.lockup} />
              <div className={styles.rule} />
              <div className={styles.serviceName}>{brand.serviceName}</div>
              <div className={styles.subtitle}>{brand.subtitle}</div>
              <div className={styles.description}>{brand.description}</div>
            </div>
          </aside>
        ) : null}

        <form className={styles.form} onSubmit={onSubmit} data-testid="sign-in-form">
          <div className={styles.heading}>{title}</div>
          {error ? (
            <div className={styles.failure} role="alert">
              {error}
            </div>
          ) : null}

          {fields.map((field) => (
            <div key={field.id}>
              <label className={styles.label} htmlFor={field.id}>
                {field.label}
              </label>
              <input
                id={field.id}
                className={styles.input}
                type={field.type}
                value={field.value}
                autoFocus={field.autoFocus}
                autoComplete={field.autoComplete}
                placeholder={field.placeholder}
                onChange={(event) => field.onChange(event.target.value)}
              />
            </div>
          ))}

          <Button
            type="submit"
            className={styles.primary}
            style={primaryAction.style}
            icon={primaryAction.busy ? <Spinner size="tiny" /> : undefined}
            disabled={primaryAction.disabled}
          >
            {primaryAction.label}
          </Button>

          <div className={styles.dividerRow}>
            <div className={styles.dividerLine} />
            <span className={styles.dividerWord}>{dividerLabel}</span>
            <div className={styles.dividerLine} />
          </div>

          <Button
            type="button"
            className={styles.secondary}
            disabled={secondaryAction.disabled}
            onClick={secondaryAction.onClick}
            icon={secondaryAction.icon}
          >
            {secondaryAction.label}
          </Button>
          {secondaryAction.status ? (
            <div className={styles.secondaryStatus}>{secondaryAction.status}</div>
          ) : null}
          <div className={styles.footnote}>{footnote}</div>
        </form>
      </div>
    </div>
  )
}
